/**
 * Tabs Component
 * Tab navigation with design system tokens and semantic variants
 */

import React from "react";
import {
  Tabs as ChakraTabs,
  TabList as ChakraTabList,
  TabPanels as ChakraTabPanels,
  Tab as ChakraTab,
  TabPanel as ChakraTabPanel,
  TabsProps as ChakraTabsProps,
  Box,
} from "@chakra-ui/react";
import { useColorModeValue } from "design-system/hooks/useColorModeValue";
import { resolveSpacing } from "design-system/foundation";
import CDS from "design-system";

// Branded types for Tabs variants and sizes
declare const __tabs_variant_brand: unique symbol;
declare const __tabs_size_brand: unique symbol;

export interface TabsVariantToken {
  readonly [__tabs_variant_brand]: true;
  readonly _value: string;
}

export interface TabsSizeToken {
  readonly [__tabs_size_brand]: true;
  readonly _value: string;
}

// Factory functions
const createTabsVariantToken = (value: string): TabsVariantToken => ({ _value: value } as TabsVariantToken);

const createTabsSizeToken = (value: string): TabsSizeToken => ({ _value: value } as TabsSizeToken);

export interface TabItem {
  label: string;
  content: React.ReactNode;
  disabled?: boolean;
}

export interface TabsProps extends Omit<ChakraTabsProps, "variant" | "size"> {
  items: TabItem[];
  variant?: TabsVariantToken;
  size?: TabsSizeToken;
  defaultIndex?: number;
  onChange?: (index: number) => void;
}

function TabsComponent({
  items,
  variant = tabsTokens.variant.line,
  size = tabsTokens.size.medium,
  defaultIndex = 0,
  onChange,
  ...props
}: TabsProps) {
  // Use color mode reactive colors
  const borderColor = useColorModeValue(CDS.Color.border.primary);
  const activeBg = useColorModeValue(CDS.Color.surface.secondary);
  const activeColor = useColorModeValue(CDS.Color.interactive.primary);
  const inactiveColor = useColorModeValue(CDS.Color.text.secondary);
  const hoverBg = useColorModeValue(CDS.Color.surface.tertiary);
  const cardBg = useColorModeValue(CDS.Color.surface.primary);
  const textInverse = useColorModeValue(CDS.Color.text.inverse);

  const getVariantStyles = (variant: TabsVariantToken) => {
    switch (variant._value) {
      case "enclosed":
        return {
          variant: "enclosed" as const,
          tabListProps: {
            border: "1px solid",
            borderColor,
            borderRadius: `${resolveSpacing(CDS.Spacing.small)} ${resolveSpacing(CDS.Spacing.small)} 0 0`,
            bg: cardBg,
          },
          tabProps: {
            _selected: {
              bg: activeBg,
              color: activeColor,
              borderBottomColor: cardBg,
            },
            _hover: {
              bg: hoverBg,
            },
          },
        };
      case "enclosed-colored":
        return {
          variant: "enclosed-colored" as const,
          tabListProps: {
            bg: cardBg,
          },
          tabProps: {
            _selected: {
              bg: activeColor,
              color: textInverse,
            },
            _hover: {
              bg: hoverBg,
            },
          },
        };
      case "soft-rounded":
        return {
          variant: "soft-rounded" as const,
          tabListProps: {
            bg: activeBg,
            borderRadius: "full",
            p: "4px",
            border: "1px solid",
            borderColor,
            display: "inline-flex",
            w: "fit-content",
          },
          tabProps: {
            _selected: {
              bg: cardBg,
              color: activeColor,
              boxShadow: "sm",
            },
          },
          containerProps: {
            borderRadius: "full",
            w: "fit-content",
            overflow: "visible",
          },
        };
      case "solid-rounded":
        return {
          variant: "solid-rounded" as const,
          tabListProps: {},
          tabProps: {
            _selected: {
              bg: activeColor,
              color: textInverse,
            },
            _hover: {
              bg: hoverBg,
            },
          },
        };
      case "line":
      default:
        return {
          variant: "line" as const,
          tabListProps: {
            borderBottomColor: borderColor,
          },
          tabProps: {
            _selected: {
              color: activeColor,
              borderBottomColor: activeColor,
            },
            _hover: {
              color: activeColor,
            },
            color: inactiveColor,
          },
        };
    }
  };

  const getSizeStyles = (size: TabsSizeToken) => {
    switch (size._value) {
      case "small":
        return {
          size: "sm" as const,
          spacing: resolveSpacing(CDS.Spacing.small),
        };
      case "large":
        return {
          size: "lg" as const,
          spacing: resolveSpacing(CDS.Spacing.large),
        };
      case "medium":
      default:
        return {
          size: "md" as const,
          spacing: resolveSpacing(CDS.Spacing.medium),
        };
    }
  };

  const variantStyles = getVariantStyles(variant);
  const sizeStyles = getSizeStyles(size);

  return (
    <Box
      {...(variantStyles.containerProps || {})}
      overflow={variantStyles.containerProps?.overflow || "hidden"}
    >
      <ChakraTabs
        variant={variantStyles.variant}
        size={sizeStyles.size}
        defaultIndex={defaultIndex}
        onChange={onChange}
        {...props}
      >
        <ChakraTabList
          {...variantStyles.tabListProps}
        >
          {items.map((item, index) => (
            <ChakraTab
              key={index}
              isDisabled={item.disabled}
              {...variantStyles.tabProps}
            >
              {item.label}
            </ChakraTab>
          ))}
        </ChakraTabList>

        <ChakraTabPanels>
          {items.map((item, index) => (
            <ChakraTabPanel
              key={index}
              px={0}
              py={sizeStyles.spacing}
            >
              {item.content}
            </ChakraTabPanel>
          ))}
        </ChakraTabPanels>
      </ChakraTabs>
    </Box>
  );
}

// Tabs tokens
const tabsTokens = {
  variant: {
    line: createTabsVariantToken("line"),
    enclosed: createTabsVariantToken("enclosed"),
    enclosedColored: createTabsVariantToken("enclosed-colored"),
    softRounded: createTabsVariantToken("soft-rounded"),
    solidRounded: createTabsVariantToken("solid-rounded"),
  } as const,

  size: {
    small: createTabsSizeToken("small"),
    medium: createTabsSizeToken("medium"),
    large: createTabsSizeToken("large"),
  } as const,
} as const;

// Public API - combines tokens and component
export const Tabs = Object.assign(TabsComponent, tabsTokens);
